/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import { debounce } from 'lodash'
import React from 'react'

import type { EuiBasicTableColumn, Search } from '@elastic/eui'
import { EuiInMemoryTable, EuiSkeletonText } from '@elastic/eui'

import type { ResourceType, RoleAssignmentsTableItem } from '@modules/role-assignments-lib/types'

type Props<T extends ResourceType> = {
  filteredItems: Array<RoleAssignmentsTableItem<T>>
  isLoading: boolean
  onSearch: (query: string) => void
  columns: Array<EuiBasicTableColumn<RoleAssignmentsTableItem<T>>>
}

const debounced = debounce((cb) => cb(), 300)

const ProjectRoleAssignmentsTable = <T extends ResourceType>({
  filteredItems,
  isLoading,
  onSearch,
  columns,
}: Props<T>) => {
  if (isLoading) {
    return <EuiSkeletonText />
  }

  const search: Search = {
    onChange: ({ query }) => debounced(() => onSearch(query?.text ?? '')),
    box: {
      incremental: true,
    },
  }

  return (
    <EuiInMemoryTable
      items={filteredItems}
      columns={columns}
      pagination={true}
      search={search}
      data-test-subj='individualRoleAssignmentsTable'
    />
  )
}

export default ProjectRoleAssignmentsTable
